import ModalCloseButton from '../../../../Component/UI/ModalCloseButton';
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { CenterView, RowView, RowView2 } from "../../../../Component/style/common_style";
import { fontStyles } from "../../../../Component/style/admin_font";

interface ProductDescriptionModalProps {
  visible: boolean;
  onClose: () => void;
  description: string;
  onSave: (updatedDesc: string) => void;
}

const ProductDescriptionModal = ({ visible, onClose, description, onSave }: ProductDescriptionModalProps) => {
  const [desc, setDesc] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteDone, setShowDeleteDone] = useState(false);

  useEffect(() => {
    if (visible) {
      setDesc(description || "");
    }
  }, [visible, description]);

  if (!visible) return null;

  const handleSave = () => {
    onSave(desc);
    onClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    setShowDeleteConfirm(false);
    setShowDeleteDone(true);
  };

  const handleDoneConfirm = () => {
    setShowDeleteDone(false);
    onSave("");
    onClose();
  };

  const isEditing = !!description; // 기존 설명이 있었는지 여부

  return (
    <>
      <ModalOverlay data-admin-modal-overlay="" onClick={onClose}>
        <ModalContent data-admin-modal="" onClick={(e) => e.stopPropagation()}>
          <ModalHeader data-admin-modal-header="">
            <div className="title">상품 설명</div>
            <CloseIcon tone="admin" aria-label="상품 설명 닫기" onClick={onClose} />
          </ModalHeader>
          

          <TextAreaContainer data-admin-modal-body="">
            <DescriptionTextArea
              placeholder="상품설명을 입력해주세요."
              value={desc}

              maxLength={255}
              onChange={(e) => setDesc(e.target.value)}
            />

            <CharacterCount>{desc.length} / 255자</CharacterCount>
          </TextAreaContainer>

          {isEditing ? (
            <FooterRow data-admin-modal-footer="">
              <DeleteButton data-admin-modal-action="" onClick={handleDeleteClick}>삭제</DeleteButton>
              <SaveButton data-admin-modal-action="" onClick={handleSave}>수정 완료</SaveButton>
            </FooterRow>
          ) : (
            <FooterRow data-admin-modal-footer="">
              <SaveButton data-admin-modal-action="" onClick={handleSave}>작성 완료</SaveButton>
            </FooterRow>
          )}
        </ModalContent>
      </ModalOverlay>

      {/* 2차 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <SubModalOverlay data-admin-modal-overlay="">
          <SubModalContent data-admin-modal="">
            <SubModalTitle data-admin-modal-header="">상품 설명 삭제</SubModalTitle>
            <SubModalBodyText data-admin-modal-body="">상품 설명을 삭제하시겠습니까?</SubModalBodyText>
            <SubModalFooter data-admin-modal-footer="">
              <SubCancelButton data-admin-modal-action="" onClick={() => setShowDeleteConfirm(false)}>취소</SubCancelButton>
              <SubConfirmButton data-admin-modal-action="" onClick={confirmDelete}>확인</SubConfirmButton>
            </SubModalFooter>
          </SubModalContent>
        </SubModalOverlay>
      )}

      {/* 3차 삭제 완료 모달 */}
      {showDeleteDone && (
        <SubModalOverlay data-admin-modal-overlay="">
          <SubModalContent data-admin-modal="">
            <SubModalTitle data-admin-modal-header="">삭제 완료</SubModalTitle>
            <SubModalBodyText data-admin-modal-body="">삭제가 완료되었습니다.</SubModalBodyText>
            <SubModalFooter data-admin-modal-footer="">
              <SubConfirmButton data-admin-modal-action="" onClick={handleDoneConfirm}>
                확인
              </SubConfirmButton>
            </SubModalFooter>
          </SubModalContent>
        </SubModalOverlay>
      )}
    </>
  );
};

export default ProductDescriptionModal;

const ModalOverlay = styled(CenterView)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1000;
`;

const ModalContent = styled.div`
  box-sizing: border-box;
  width: 90%;
  max-width: 32rem;
  background-color: var(--admin-theme-surface, white);
  border-radius: 12px;
  padding: 1.8rem;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const ModalHeader = styled(CenterView)`
  min-height: 44px;
  flex-shrink: 0;
  .title { padding: 0 48px; overflow-wrap: anywhere; }
  position: relative;
  width: 100%;
  margin-bottom: 1rem;
  .title {
    font-size: 18px;
    ${fontStyles.Bold}
    color: var(--admin-ink);
    text-align: center;
  }
`;

const CloseIcon = styled(ModalCloseButton)`  position: absolute;
  right: 0rem;
  top: 50%;
  transform: translateY(-50%);
`;

const HeaderDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--admin-border);
  margin-bottom: 1.2rem;
`;

const TextAreaContainer = styled.div`
  margin-bottom: 1.8rem;
  width: 100%;
`;

const DescriptionTextArea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  height: 22rem;
  border: none;
  resize: none;
  outline: none;
  font-size: 15px;
  color: var(--admin-ink);
  ${fontStyles.regular}
  &::placeholder {
    color: var(--admin-theme-muted, #c6c6c6);
  }
`;

const CharacterCount = styled.div`
  margin-top: 8px;
  color: var(--admin-theme-muted, #77717f);
  font-size: 12px;
  text-align: right;
`;

const FooterRow = styled(RowView)`
  justify-content: space-between;
  width: 100%;
`;

const SaveButton = styled(CenterView)`
  height: 3.2rem;
  background: linear-gradient(135deg, var(--admin-accent), var(--admin-accent-deep));
  color: white;
  border-radius: 8px;
  font-size: 15px;
  ${fontStyles.Bold}
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const DeleteButton = styled(CenterView)`
  width: 30%;
  height: 3.2rem;
  background-color: var(--admin-theme-raised, #F0F0F0);
  color: var(--admin-muted);
  border-radius: 8px;
  font-size: 15px;
  ${fontStyles.Bold}
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background-color: var(--admin-theme-raised, #e2e2e2);
  }
`;

/* 2, 3차 서브 모달 전용 스타일드 컴포넌트 */
const SubModalOverlay = styled(CenterView)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1100; /* 메인 모달보다 높은 레이어 */
`;

const SubModalContent = styled.div`
  box-sizing: border-box;
  width: 85%;
  max-width: 22rem;
  background-color: var(--admin-theme-surface, white);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.12);
`;

const SubModalTitle = styled.div`
  font-size: 16px;
  ${fontStyles.Bold}
  color: var(--admin-ink);
  margin-bottom: 1.2rem;
  text-align: center;
`;

const SubModalBodyText = styled.div`
  font-size: 14px;
  color: var(--admin-theme-text, #454545);
  ${fontStyles.medium}
  margin-bottom: 1.8rem;
  text-align: center;
`;

const SubModalFooter = styled(RowView)`
  justify-content: space-between;
  width: 100%;
`;

const SubConfirmButton = styled(CenterView)`
  width: 48%;
  height: 2.8rem;
  background: linear-gradient(135deg, var(--admin-accent), var(--admin-accent-deep));
  color: white;
  border-radius: 8px;
  font-size: 14px;
  ${fontStyles.Bold}
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const SubCancelButton = styled(CenterView)`
  width: 48%;
  height: 2.8rem;
  background-color: var(--admin-theme-raised, #F0F0F0);
  color: var(--admin-muted);
  border-radius: 8px;
  font-size: 14px;
  ${fontStyles.Bold}
  cursor: pointer;
  &:hover {
    background-color: var(--admin-theme-raised, #e2e2e2);
  }
`;
